import PropTypes from "prop-types"
import React from "react"

import Button from "src/components/Button"
import Modal from "src/components/Modal"

// Color mapping for each dock type - extending from AvailabilityBar
const TYPE_COLORS = {
  Dockage: {
    dot: "bg-blue-600",
    bar: "bg-blue-600",
  },
  Moorage: {
    dot: "bg-teal-500",
    bar: "bg-teal-500",
  },
  Storage: {
    dot: "bg-supplementary-violet-500",
    bar: "bg-supplementary-violet-500",
  },
  Other: {
    dot: "bg-gray-500",
    bar: "bg-gray-500",
  },
}

// Format number with commas
const formatNumber = (num) => Math.abs(num).toLocaleString()

// Get unit suffix for display
const getUnitSuffix = (unit) => {
  switch (unit) {
    case "linear":
      return "'"
    case "sqft":
      return " sqft"
    default:
      return ""
  }
}

// Format value with unit
const formatValueWithUnit = (value, unit) => {
  const prefix = value < 0 ? "-" : ""
  return `${prefix}${formatNumber(value)}${getUnitSuffix(unit)}`
}

// Calculate usage percentage (how much is USED)
const getUsagePercentage = (remaining, total) => {
  if (total <= 0) return 0
  const used = total - remaining
  return Math.min(Math.max((used / total) * 100, 0), 100)
}

// Calculate available percentage
const getAvailablePercentage = (remaining, total) => {
  if (total <= 0) return 0
  return Math.round((remaining / total) * 100)
}

// Calculate booked percentage (for overbooked)
const getBookedPercentage = (remaining, total) => {
  if (total <= 0) return 0
  const overbooked = Math.abs(remaining)
  return Math.round(((total + overbooked) / total) * 100)
}

// Build type summary text
const buildTypeSummary = (spaceGroups) => {
  // Aggregate by unit type
  const aggregated = { count: 0, linear: 0, sqft: 0 }
  const hasUnit = { count: false, linear: false, sqft: false }

  spaceGroups.forEach((sg) => {
    aggregated[sg.unit] += sg.remaining
    hasUnit[sg.unit] = true
  })

  const parts = []

  if (hasUnit.count) {
    const val = aggregated.count
    if (val < 0) {
      parts.push(`${formatNumber(val)} spaces overbooked`)
    } else {
      parts.push(`${formatNumber(val)} spaces remaining`)
    }
  }

  if (hasUnit.linear) {
    const val = aggregated.linear
    if (val < 0) {
      parts.push(`${formatNumber(val)}' overbooked`)
    } else {
      parts.push(`${formatNumber(val)}' linear remaining`)
    }
  }

  if (hasUnit.sqft) {
    const val = aggregated.sqft
    if (val < 0) {
      parts.push(`${formatNumber(val)} sqft overbooked`)
    } else {
      parts.push(`${formatNumber(val)} sqft remaining`)
    }
  }

  return parts.join(", ")
}

// Check if any aggregated value is negative
const hasOverbookedSummary = (spaceGroups) => {
  const aggregated = { count: 0, linear: 0, sqft: 0 }
  spaceGroups.forEach((sg) => {
    aggregated[sg.unit] += sg.remaining
  })
  return aggregated.count < 0 || aggregated.linear < 0 || aggregated.sqft < 0
}

// Space Group Card component
const SpaceGroupCard = ({ spaceGroup, barColorClass }) => {
  const { name, total, remaining, unit } = spaceGroup
  const isOverbooked = remaining < 0
  const percentage = getUsagePercentage(remaining, total)

  return (
    <div
      className={`rounded p-3.5 transition-colors ${
        isOverbooked
          ? "bg-red-50 hover:bg-red-100"
          : "bg-gray-100 hover:bg-gray-200"
      }`}
    >
      {/* Card Header */}
      <div className="mb-2.5 flex items-start justify-between">
        <span className="mr-2 flex-1 text-sm font-semibold leading-tight text-gray-900">
          {name}
        </span>
        <span className="flex-shrink-0 text-xs text-gray-500">
          <span className="font-mono">{formatValueWithUnit(total, unit)}</span>{" "}
          total
        </span>
      </div>

      {/* Progress Bar */}
      <div
        className={`mb-2 h-1.5 overflow-hidden rounded-full ${
          isOverbooked ? "bg-white/50" : "bg-white/60"
        }`}
      >
        <div
          className={`duration-400 h-full rounded-full transition-all ${
            isOverbooked ? "bg-red-600" : barColorClass
          }`}
          style={{ width: `${isOverbooked ? 100 : percentage}%` }}
        />
      </div>

      {/* Remaining Display */}
      <div className="flex items-center justify-between">
        <div>
          <span
            className={`font-mono text-sm font-semibold ${
              isOverbooked ? "text-red-600" : "text-gray-900"
            }`}
          >
            {formatValueWithUnit(remaining, unit)}
          </span>
          <span
            className={`ml-1 text-xs ${
              isOverbooked ? "text-red-600" : "text-gray-500"
            }`}
          >
            {isOverbooked ? "overbooked" : "remaining"}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          {isOverbooked
            ? `${getBookedPercentage(remaining, total)}% booked`
            : `${getAvailablePercentage(remaining, total)}% available`}
        </span>
      </div>
    </div>
  )
}

SpaceGroupCard.propTypes = {
  spaceGroup: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    remaining: PropTypes.number.isRequired,
    unit: PropTypes.oneOf(["count", "linear", "sqft"]).isRequired,
  }).isRequired,
  barColorClass: PropTypes.string.isRequired,
}

// Type Section component
const TypeSection = ({ typeName, spaceGroups, isLast }) => {
  const colors = TYPE_COLORS[typeName] || TYPE_COLORS.Other
  const summary = buildTypeSummary(spaceGroups)
  const hasOverbooked = hasOverbookedSummary(spaceGroups)

  // Sort space groups by name
  const sortedGroups = [...spaceGroups].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return (
    <div className={isLast ? "" : "mb-7"}>
      {/* Type Header */}
      <div className="mb-3.5 flex items-center gap-2.5 border-b border-gray-200 pb-2.5">
        <div
          className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${colors.dot}`}
        />
        <span className="text-sm font-semibold text-gray-900">{typeName}</span>
        <span
          className={`ml-auto text-xs ${
            hasOverbooked ? "text-red-600" : "text-gray-500"
          }`}
        >
          {summary}
        </span>
      </div>

      {/* Space Groups Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sortedGroups.map((sg) => (
          <SpaceGroupCard
            key={sg.id}
            spaceGroup={sg}
            barColorClass={colors.bar}
          />
        ))}
      </div>
    </div>
  )
}

TypeSection.propTypes = {
  typeName: PropTypes.string.isRequired,
  spaceGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      remaining: PropTypes.number.isRequired,
      unit: PropTypes.oneOf(["count", "linear", "sqft"]).isRequired,
    })
  ).isRequired,
  isLast: PropTypes.bool,
}

// Format date for display
const formatDate = (dateString) => {
  const date = new Date(dateString + "T00:00:00")
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

// Main Modal component
const AvailabilityDetailsModal = ({ isOpen, onClose, date, types }) => {
  // Filter out empty types
  const nonEmptyTypes = types.filter((t) => t.spaceGroups.length > 0)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <Modal.Header title="Availability Details" />
      <Modal.Body>
        {/* Date subtitle */}
        <p className="mb-5 text-sm text-gray-500">{formatDate(date)}</p>

        {nonEmptyTypes.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No availability data available.
          </div>
        ) : (
          nonEmptyTypes.map((typeData, index) => (
            <TypeSection
              key={typeData.type}
              typeName={typeData.type}
              spaceGroups={typeData.spaceGroups}
              isLast={index === nonEmptyTypes.length - 1}
            />
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

AvailabilityDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  types: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      spaceGroups: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          name: PropTypes.string.isRequired,
          total: PropTypes.number.isRequired,
          remaining: PropTypes.number.isRequired,
          unit: PropTypes.oneOf(["count", "linear", "sqft"]).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
}

export default AvailabilityDetailsModal
